import {
  CircleDollarSign,
  Repeat2,
  ShieldCheck,
  Sprout,
  TabletSmartphone,
} from 'lucide-react';
import Image from 'next/image';
import { ProductCategoryWarranty } from '@lambda/apis/src/category/types';
import { ReebeloConditions } from '@lambda/reebelo';
import { handleFreeReturn } from '@lambda/commons/helpers';
import classNames from 'classnames';
import settings from '@/settings';
import i18n from '@/settings/i18n';
import TrustpilotStar from './trustpilot/TrustpilotStar';
import { formatWarrantyInfo } from '@/lib/warranty';
import { useCompanyReviewsContext } from '@/utils/hooks/reviews/useCompanyReviewsContext';
import { Stars } from '../product/sections/main/right/product/ReviewsSummary';
import { formatStoreDecimal, formatStoreNumber } from '@/lib/intl-format';

const t = i18n.init('homepage');

const Ratings = () => {
  const { company, isLoading } = useCompanyReviewsContext();

  return (
    <div className="flex items-center gap-1 text-xs xxs:text-sm lg:text-base">
      <div className="flex items-center gap-1 xs:gap-1">
        <span
          className={classNames('font-bold', {
            'blur-sm': isLoading,
          })}
        >
          {formatStoreDecimal(company?.business?.score?.stars ?? 4.7)}
        </span>
        <span className="hidden xxs:inline">{t`out of`} </span>
        <span className="xxs:hidden">/</span>
        <span>5</span>
      </div>
      <div className="items-center gap-1">
        <span>{t`based on`} </span>
        <span
          className={classNames('font-bold', {
            'blur-sm': isLoading,
          })}
        >
          {formatStoreNumber(
            company?.business?.numberOfReviews?.total ?? 70000,
          )}{' '}
          {t`reviews`}
        </span>
      </div>
    </div>
  );
};

const Content = ({
  categoryWarranty,
  condition,
}: {
  categoryWarranty?: ProductCategoryWarranty | null;
  condition?: string;
}) => {
  const formattedWarrantyInfo = formatWarrantyInfo(categoryWarranty);

  const isRefurbishedAndSG =
    settings.store === 'quista' && condition !== ReebeloConditions.BrandNew;

  const formattedCategoryWarranty = isRefurbishedAndSG
    ? '3 Months'
    : formattedWarrantyInfo;

  const CONTENT = [
    {
      icon: CircleDollarSign,
      title: t`Save up to 70%`,
    },
    {
      icon: ShieldCheck,
      title: t`${{ warranty: t(formattedCategoryWarranty) }} Warranty`,
    },
    {
      icon: TabletSmartphone,
      title: t`Quality Tested by Experts`,
    },
    {
      icon: Repeat2,
      title: t`Risk-free ${{
        freeReturnDays: handleFreeReturn(
          settings.store,
          settings.free_return_days,
        ),
      }}-Day Trial`,
    },
    {
      icon: Sprout,
      title: t`Save up to 90% CO<span class="align-sub text-xxs">2</span> vs new`,
    },
  ];

  return (
    <div className="relative w-full rounded-t-xl bg-white p-2 text-sm xs:p-4 md:w-fit lg:min-w-[350px]">
      <div className="absolute -right-3 -top-6 h-12 w-12 md:-right-7 md:-top-7 md:h-14 md:w-14 lg:-right-8 lg:-top-8 lg:h-16 lg:w-16">
        <Image
          src={'/icons/logo/logo-black.svg'}
          alt="Reebelo"
          height={100}
          width={100}
          className="h-full w-full"
        />
      </div>
      {CONTENT.map((item) => {
        const Icon = item.icon;

        return (
          <div
            key={item.title}
            className="flex gap-2 border-b p-3 last:mb-0 last:border-none"
          >
            <Icon className="h-5 w-5 shrink-0" strokeWidth={1.5} />
            <div
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: item.title }}
            />
          </div>
        );
      })}
    </div>
  );
};

const TrustpilotStars = () => {
  const { company } = useCompanyReviewsContext();

  return (
    <div className="flex items-center gap-2 text-xs xxs:text-sm lg:text-base">
      <Stars
        stars={company?.business?.score?.stars ?? 4.7}
        className="h-[18px] lg:h-5"
      />
      <div className="flex items-center gap-1">
        <TrustpilotStar height={18} />
        <span className="font-medium">{t`Trustpilot`}</span>
      </div>
    </div>
  );
};

const MarketplaceV2 = ({
  categoryWarranty,
  condition,
}: {
  categoryWarranty?: ProductCategoryWarranty | null;
  condition?: string;
}) => (
  <div className="reebelo-container py-10">
    <div className="flex items-center justify-between rounded-2xl bg-[linear-gradient(253deg,_#92F7FA_-5.63%,_#92F7FA_62.21%,_#92FAD5_88.08%)] shadow-[0_0_40px_rgba(0,0,0,0.08)] md:bg-[linear-gradient(88deg,_#92F7FA_0%,_#92F7FA_72.4%,_#92FAD5_100%)]">
      <div className="mx-auto flex w-full flex-col items-center justify-end md:max-w-[95%] md:flex-row lg:max-w-[90%]">
        <div className="w-full flex-1 p-5 sm:p-8 md:pl-8 lg:w-auto lg:p-0">
          <h2 className="text-2xl font-bold leading-tight md:mx-0 md:text-left lg:text-3xl">
            {t`Premium Refurbished.`}
            <br /> {t`Trusted Quality.`}
          </h2>
          <div className="mt-4 space-y-1 text-sm leading-none xxs:mt-6 xxs:space-y-2">
            <Ratings />
            <TrustpilotStars />
          </div>
        </div>
        <div className="flex w-full flex-1 items-center justify-center px-5 sm:px-8 md:w-auto md:justify-end md:px-8 md:pt-5">
          <Content categoryWarranty={categoryWarranty} condition={condition} />
        </div>
      </div>
    </div>
  </div>
);

export default MarketplaceV2;
